/* import __COLOCATED_TEMPLATE__ from './input-text.hbs'; */
import Component from '@ember/component';
// eslint-disable-next-line import/default
import FieldKit from 'field-kit';
import { computed } from '@ember/object';

/**
 * This component is a wrapper around a market-field and market-input-text component.
 * It supports fieldkit formatters and showing validation errors.
 *
 * ```
 * <Prism::InputText
 *   @value={{this.nameOfValueProperty}}
 *   @inputId="my-id"
 *   @inputClass="some-class some-other-class"
 *   @label="Name"
 *   @placeholder="Put you name here"
 *   @onChange={{action (mut nameOfValueProperty))}}
 *   @formGroupError={{false}}
 * />
 * ```
 *
 */
export default Component.extend({
  tagName: '',
  /**
   * @param {String} inputId - ID to apply to the underlying input field.
   */
  inputId: null,
  /**
   * @param {String} inputClass - Class to apply to the underlying input field
   */
  inputClass: null,
  /**
   * @param {String} inputType - "type" to apply to the underlying input field
   */
  inputType: 'text',
  /**
   * @param {Number} maxlength - maxlength of the underlying input
   */
  maxlength: null,
  /**
   * @param {String} class - Class to apply to the underlying market-field
   */
  class: null,
  /**
   * @param {String} label - Label for the input
   */
  label: null,
  /**
   * @param {String} value - The value passed. This is only relevant to set the initial value of thea field.
   */
  value: null,
  /**
   * @param {String} placeholder - Placeholder of the underlying input field
   */
  placeholder: null,
  /**
   * @param {String} tooltipText - Text to populate a tooltip, only appears if this is passed in
   */
  tooltipText: null,
  /**
   * @param {String} name - name for the input
   */
  name: null,
  /**
   * @param {Boolean} readonly - is the input readonly
   */
  readonly: false,
  /**
   * @param {Boolean} disabled - is the input disabled
   */
  disabled: false,
  /**
   * @param {Boolean} focused - is the input focused
   */
  focused: false,
  /**
   * @param {Boolean} formGroupError - hide the input's error message, since it will shown once for the whole group
   */
  formGroupError: false,
  /**
   * Optional attribute to determine whether the field should have autocomplete
   * enabled. Note that autocomplete does not play nicely with masking -- some
   * browsers attempt to auto fill the field with the •••• characters.
   *
   * @argument autocomplete
   * @type {String}
   */
  autocomplete: 'off',
  /**
   * @param {Object} validation
   * @param {Boolean} validation.isInvalid - whether or not the field is invalid
   * @param {Boolean} validation.message - validation message to display below the field
   */
  validation: null,
  /**
   * @param {Boolean} showValidation - whether or not the validation is ready to be shown
   */
  showValidation: false,
  /**
   * @param {FieldKit.Formatter} [formatter] - optional formatter to apply to the field value
   */
  formatter: null,
  /**
   * @param {Boolean} autofocus - whether or not the input should focus on page load
   */
  autofocus: false,
  /**
   * @param {String} [tooltipContentClass] optional id to add to the market-tooltip component
   */
  tooltipId: null,
  /**
   * @param {String} [tooltipContentClass] optional class to add to the content of the tooltip
   */
  tooltipContentClass: '',
  /**
   * @param {Boolean} [hasTooltipBody] set to true to enable showing yielded tooltip bodies
   */
  hasTooltipBody: false,
  /**
   * @param {Boolean} [popoverPlacement] popover placement passed into the market-tooltip component
   */
  popoverPlacement: 'bottom-end',
  /**
   * @param {Action} [onChange] action triggered when the input value changes
   */
  onChange() {},
  /**
   * @param {Function} [onInputLoad] action triggered when the market-input-text has loaded
   */
  onInputLoad() {},
  // Internal value passed to the input.
  _internalValue: computed('formatter', 'value', {
    get() {
      if (this.__internalValueIsOverridden) {
        return this.__internalValueOverride;
      }
      if (this.formatter) {
        return this.formatter.format(this.value);
      } else {
        return this.value;
      }
    },
    set(_field, value) {
      this.__internalValueIsOverridden = true;
      this.__internalValueOverride = value;
      return value;
    }
  }),
  showError: computed('showValidation', 'validation.isInvalid', function () {
    if (!this.validation) {
      return false;
    }
    return this.validation.isInvalid && this.showValidation;
  }),
  init(...args) {
    this._super(...args);
  },
  actions: {
    _onChange(event) {
      const value = event.detail.value;
      if (this.onChange) {
        this.onChange(value, event);
      }
    },
    _inputDidLoad(event) {
      if (this.formatter) {
        this.field = new FieldKit.TextField(event.detail.input, this.formatter);
        // FieldKit workaround, see https://github.com/square/field-kit/issues/62
        if (document.activeElement === this.field.element) {
          document.activeElement.blur();
        }
      }
      if (this.onInputLoad) {
        this.onInputLoad(event);
      }
    },
    popoverOpened() {
      this.set('popoverIsActive', true);
    },
    popoverClosed() {
      this.set('popoverIsActive', false);
    }
  }
});